import { useQuery } from "@tanstack/react-query";
import {
  Anchor as PopoverAnchor,
  Content as PopoverContent,
  Portal as PopoverPortal,
} from "@radix-ui/react-popover";
import { type ReactElement, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { errorMessage } from "@/api/problem";
import type { PickerOption, PickerProps } from "@/shared/components/pickers";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/form";
import { Popover } from "@/shared/ui/popover";
import { directionFor } from "@/i18n";

/** One editable, keyboard-operated control for remotely searched entity options. */
export function SearchCombobox({
  id,
  value,
  onChange,
  disabled,
  required,
  placeholder,
  selectedLabel,
  queryKey,
  load,
  loadSelected,
  ...aria
}: PickerProps & {
  selectedLabel?: string;
  queryKey: readonly unknown[];
  load: (q: string) => Promise<PickerOption[]>;
  loadSelected: (id: string) => Promise<PickerOption>;
}): ReactElement {
  const { t, i18n } = useTranslation();
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const anchorRef = useCallback((element: HTMLDivElement | null): void => {
    if (element) setPortalContainer(element.closest<HTMLElement>('[role="dialog"]'));
  }, []);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(-1);
  const [selected, setSelected] = useState<PickerOption | null>(null);
  const query = useQuery({
    queryKey: [...queryKey, "combobox", search],
    queryFn: () => load(search),
    staleTime: 60_000,
  });
  const current = useQuery({
    queryKey: [...queryKey, "selected", value],
    queryFn: () => loadSelected(value ?? ""),
    enabled: Boolean(value) && !selectedLabel && selected?.id !== value,
    staleTime: 60_000,
  });
  const options = query.data ?? [];
  useEffect(() => {
    if (open && active >= 0) {
      document.getElementById(`${id}-option-${active}`)?.scrollIntoView?.({ block: "nearest" });
    }
  }, [open, active, id]);
  const label = !value
    ? ""
    : ((selected?.id === value ? selected.label : undefined) ??
      selectedLabel ??
      current.data?.label ??
      options.find((option) => option.id === value)?.label ??
      "");
  const choose = (option: PickerOption): void => {
    setSelected(option);
    onChange(option.id);
    setOpen(false);
    setSearch("");
  };
  const close = (): void => {
    setOpen(false);
    setSearch("");
    setActive(-1);
  };
  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <PopoverAnchor asChild>
        <div
          ref={anchorRef}
          className="relative min-w-0"
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) close();
          }}
        >
          <div className="flex min-w-0 items-center gap-1">
            <Input
              id={id}
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={open}
              aria-controls={`${id}-options`}
              aria-activedescendant={open && options[active] ? `${id}-option-${active}` : undefined}
              aria-invalid={aria["aria-invalid"]}
              aria-label={aria["aria-label"]}
              aria-busy={query.isFetching || current.isFetching}
              value={open ? search : label}
              placeholder={placeholder ?? t("feedbackAssets.search")}
              disabled={disabled}
              required={required}
              onFocus={() => {
                setOpen(true);
                setActive(-1);
              }}
              onClick={() => {
                if (!open) {
                  setSearch("");
                  setOpen(true);
                  setActive(-1);
                }
              }}
              onChange={(event) => {
                setSearch(event.target.value);
                setOpen(true);
                setActive(-1);
              }}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  event.preventDefault();
                  event.stopPropagation();
                  close();
                }
                if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                  event.preventDefault();
                  setOpen(true);
                  setActive((index) =>
                    Math.max(
                      0,
                      Math.min(
                        options.length - 1,
                        index < 0 && event.key === "ArrowUp"
                          ? options.length - 1
                          : index + (event.key === "ArrowDown" ? 1 : -1),
                      ),
                    ),
                  );
                }
                if (open && (event.key === "Home" || event.key === "End")) {
                  event.preventDefault();
                  setActive(event.key === "Home" ? 0 : options.length - 1);
                }
                if (event.key === "Enter" && open) {
                  event.preventDefault();
                  if (options[active]) choose(options[active]);
                }
              }}
            />
            {value && !required ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                aria-label={t("feedbackAssets.clearSelection")}
                data-combobox-clear={id}
                onClick={() => {
                  onChange(null);
                  setSelected(null);
                  close();
                }}
              >
                {t("feedbackAssets.clear")}
              </Button>
            ) : null}
          </div>
        </div>
      </PopoverAnchor>
      <PopoverPortal container={portalContainer ?? undefined}>
        <PopoverContent
          role="presentation"
          dir={directionFor(i18n.resolvedLanguage ?? "en")}
          align="start"
          sideOffset={6}
          collisionPadding={12}
          className="z-50 max-h-56 w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-1.5rem)] overflow-y-auto overscroll-contain rounded-lg border bg-background text-foreground shadow-lg outline-none"
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => {
            event.preventDefault();
            close();
          }}
          onInteractOutside={(event) => {
            if (
              event.target instanceof HTMLElement &&
              (event.target.id === id ||
                event.target.closest("button")?.getAttribute("data-combobox-clear") === id)
            )
              event.preventDefault();
          }}
        >
          <ul
            id={`${id}-options`}
            role="listbox"
            aria-label={aria["aria-label"] ?? t("feedbackAssets.results")}
          >
            {options.map((option, index) => (
              <li
                key={option.id}
                id={`${id}-option-${index}`}
                role="option"
                aria-selected={value === option.id}
                className={`cursor-pointer px-3 py-2 text-start text-sm ${index === active ? "bg-accent" : ""}`}
                onPointerDown={(event) => event.preventDefault()}
                onPointerMove={() => setActive(index)}
                onClick={() => choose(option)}
              >
                {option.label}
                {option.hint ? (
                  <span className="ms-2 text-muted-foreground">{option.hint}</span>
                ) : null}
              </li>
            ))}
          </ul>
          {query.isFetching ? (
            <p role="status" className="p-3 text-sm">
              {t("feedbackAssets.loading")}
            </p>
          ) : null}
          {!query.isFetching && !query.isError && options.length === 0 ? (
            <p role="status" className="p-3 text-sm">
              {t("feedbackAssets.noResults")}
            </p>
          ) : null}
        </PopoverContent>
      </PopoverPortal>
      {query.isError || current.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {errorMessage(query.error ?? current.error)}
        </p>
      ) : null}
    </Popover>
  );
}
